import round from 'lodash/round';
import sum from 'lodash/sum';

import { CourseData, HoleData } from '../data_types/types';
import { getMyHoleAverage, getStrokesGainedOnElite, getStrokesGainedOnField, getTotalAces } from './transformers/hole_data';

/*****************************************************************************
 ** Score buckets relative to par
*****************************************************************************/

export const SCORE_BUCKETS = [
    { key: 'ace', label: 'Aces' },
    { key: 'eagle', label: 'Eagles+' },
    { key: 'birdie', label: 'Birdies' },
    { key: 'par', label: 'Pars' },
    { key: 'bogey', label: 'Bogeys' },
    { key: 'double', label: 'Double+' },
] as const;

export type ScoreBucketKey = typeof SCORE_BUCKETS[number]['key'];

/** An ace always counts as an ace, even when it is also a birdie or eagle. */
export const bucketOf = (score: number, par: number): ScoreBucketKey => {
    if (score === 1) return 'ace';
    const toPar = score - par;
    if (toPar <= -2) return 'eagle';
    if (toPar === -1) return 'birdie';
    if (toPar === 0) return 'par';
    if (toPar === 1) return 'bogey';
    return 'double';
};

export type ScoreDistribution = {
    total: number;
    buckets: { key: ScoreBucketKey; label: string; count: number; percent: number }[];
    underParPercent: number;
};

export const getScoreDistribution = (holes: HoleData[]): ScoreDistribution => {
    const counts: Record<ScoreBucketKey, number> = { ace: 0, eagle: 0, birdie: 0, par: 0, bogey: 0, double: 0 };
    holes.forEach(hole => hole.scores.forEach(score => { counts[bucketOf(score, hole.par)] += 1; }));
    const total = sum(Object.values(counts));
    const pct = (n: number) => (total ? round((n / total) * 100, 1) : 0);
    return {
        total,
        buckets: SCORE_BUCKETS.map(b => ({ ...b, count: counts[b.key], percent: pct(counts[b.key]) })),
        underParPercent: pct(counts.ace + counts.eagle + counts.birdie),
    };
};

/*****************************************************************************
 ** Per hole view model
*****************************************************************************/

export type HoleRow = HoleData & {
    id: string;
    course: string;
    holeNum: number;
    myAvg: number;
    aces: number;
    bestScore: number;
    vsField: number;       // positive = better than the field average
    vsElite: number;       // positive = better than elite
    fieldToPar: number;    // how hard the hole plays for everyone
    underParPercent: number;
};

export const toHoleRows = (holes: HoleData[]): HoleRow[] =>
    holes.map(hole => {
        const match = hole.fullName.match(/^(.*)\s(\d+)$/);
        const underPar = hole.scores.filter(s => s < hole.par).length;
        return {
            ...hole,
            id: hole.shorthand,
            course: match ? match[1] : hole.fullName,
            holeNum: match ? Number(match[2]) : 0,
            myAvg: getMyHoleAverage(hole),
            aces: getTotalAces(hole),
            bestScore: hole.scores.length ? Math.min(...hole.scores) : 0,
            vsField: getStrokesGainedOnField(hole),
            vsElite: getStrokesGainedOnElite(hole),
            fieldToPar: round(hole.field_avg - hole.par, 2),
            underParPercent: hole.timesPlayed ? round((underPar / hole.timesPlayed) * 100) : 0,
        };
    });

/**
 * Minimum plays a hole needs before it can be ranked as a best or toughest
 * hole. Below this a single good or bad score would dominate the ranking.
 */
export const MIN_RANKED_HOLE_PLAYS = 3;

/** Holes with enough plays to rank, or every hole if none qualify yet. */
export const rankableHoles = (holes: HoleRow[]) => {
    const qualified = holes.filter(h => h.timesPlayed >= MIN_RANKED_HOLE_PLAYS);
    return qualified.length ? qualified : holes;
};

/*****************************************************************************
 ** Per course view model (positive = better, like the hole rows)
*****************************************************************************/

export type CourseRow = CourseData & {
    id: string;
    vsField: number;
    vsElite: number;
};

export const toCourseRows = (courses: CourseData[]): CourseRow[] =>
    courses.map(course => ({
        ...course,
        id: course.shortname,
        vsField: round(-course.me_vs_field, 2),
        vsElite: round(-course.me_vs_elite, 2),
    }));
